import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Maximize2, Download, ZoomIn, ZoomOut, Maximize, X } from "lucide-react";

// items: [{key,label}]  maps: {key: dataUri}  onHd?: (key)=>void
export function MapGrid({ items, maps, onHd }) {
  const [full, setFull] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="map-grid">
        {items.map((it) =>
          maps?.[it.key] ? (
            <div key={it.key} data-testid={`map-card-${it.key}`}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden flex flex-col">
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-100 dark:border-slate-700 truncate">
                {it.label}
              </div>
              <button data-testid={`map-open-${it.key}`} onClick={() => setFull({ ...it, src: maps[it.key] })}
                className="bg-slate-50 dark:bg-slate-900 p-2 cursor-zoom-in">
                <img src={maps[it.key]} alt={it.label} loading="lazy"
                  className="w-full h-auto object-contain" style={{ maxHeight: "72vh" }} />
              </button>
              <div className="flex border-t border-slate-100 dark:border-slate-700">
                <button data-testid={`map-view-${it.key}`} onClick={() => setFull({ ...it, src: maps[it.key] })}
                  className="flex-1 h-9 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center gap-1.5">
                  <Maximize2 size={13} /> View Full Screen
                </button>
                {onHd && (
                  <button data-testid={`map-hd-${it.key}`} onClick={() => onHd(it.key)}
                    className="flex-1 h-9 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 border-l border-slate-100 dark:border-slate-700 flex items-center justify-center gap-1.5">
                    <Download size={13} /> Download HD
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div key={it.key} className="aspect-[4/5] grid place-items-center text-slate-300 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-sm">
              Not available
            </div>
          )
        )}
      </div>
      <FullViewer full={full} onClose={() => setFull(null)} onHd={onHd} />
    </>
  );
}

function FullViewer({ full, onClose, onHd }) {
  const [zoom, setZoom] = useState(1);
  const close = () => { setZoom(1); onClose(); };
  return (
    <Dialog open={!!full} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[93vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden" data-testid="map-fullscreen">
        <div className="flex items-center justify-between px-4 h-12 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shrink-0">
          <DialogTitle className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
            {full?.label}
          </DialogTitle>
          <div className="flex items-center gap-1">
            <IconBtn testid="fs-fit" title="Fit map" onClick={() => setZoom(1)}><Maximize size={16} /></IconBtn>
            <IconBtn testid="fs-zoom-out" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><ZoomOut size={16} /></IconBtn>
            <span className="text-xs text-slate-500 w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <IconBtn testid="fs-zoom-in" title="Zoom in" onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))}><ZoomIn size={16} /></IconBtn>
            {onHd && full && <IconBtn testid="fs-hd" title="Download HD" onClick={() => onHd(full.key)}><Download size={16} /></IconBtn>}
            <IconBtn testid="fs-close" title="Close" onClick={close}><X size={16} /></IconBtn>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 grid place-items-center p-3">
          {full && (
            <img src={full.src} alt={full.label}
              className="max-w-full max-h-full object-contain transition-transform duration-150"
              style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const IconBtn = ({ children, onClick, title, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className="h-8 w-8 grid place-items-center rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
    {children}
  </button>
);
